import { MacWindow } from './MacWindow';
import React, { useState, useEffect } from 'react';
import { useSaaSContext } from '../../store/SaaSContext';
import { SavedTemplate, VdpTemplate, VdpLayout, Unit } from '../../types';
import { 
  getSavedTemplates, 
  saveUserTemplate, 
  deleteUserTemplate, 
  exportTemplateAsJSON,
  STARTER_PRESETS
} from '../../utils/presetTemplates';
import { 
  FolderOpen, 
  Save, 
  Trash2, 
  Download, 
  Upload, 
  Search, 
  Layout, 
  Sparkles, 
  RotateCcw,
  Cloud,
  CloudUpload,
  HardDrive,
  LogIn,
  LogOut,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Copy,
  Check,
  ExternalLink,
  Info
} from 'lucide-react';

interface UserAuthDashboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadTemplate: (template: VdpTemplate, layout?: VdpLayout, unit?: Unit) => void;
}

export const UserAuthDashboardModal: React.FC<UserAuthDashboardModalProps> = ({
  isOpen,
  onClose,
  onLoadTemplate
}) => {
  const { 
    startFreshProject, 
    template: currentTemplate, 
    layout: currentLayout, 
    appUnit: currentUnit,
    user,
    setShowLocalAuthModal
  } = useSaaSContext();

  const [activeTab, setActiveTab] = useState<'local' | 'presets'>('local');

  // Templates state
  const [localTemplates, setLocalTemplates] = useState<SavedTemplate[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDesc, setNewTemplateDesc] = useState('');
  const [toastMessage, setToastMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadAllTemplates();
      setActiveTab('local');
    }
  }, [isOpen]);

  const loadAllTemplates = async () => {
    try {
      const local = await getSavedTemplates();
      setLocalTemplates(local);
    } catch (e) {
      console.error("Failed to load local templates:", e);
    }
  };

  const showToast = (text: string, type: 'success' | 'error' = 'success') => {
    setToastMessage({ text, type });
    setTimeout(() => setToastMessage(null), 3500);
  };

  const handleSaveCurrent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTemplateName.trim()) return;

    if (!user) {
      showToast("Please sign in or create a local account to save templates to your local database.", 'error');
      setShowLocalAuthModal(true);
      return;
    }

    setIsSaving(true);

    try {
      await saveUserTemplate(
        newTemplateName.trim(),
        currentTemplate,
        currentLayout,
        currentUnit,
        newTemplateDesc.trim() || undefined
      );
      showToast(`Template "${newTemplateName}" saved to Local Storage!`);
      setNewTemplateName('');
      setNewTemplateDesc('');
      setShowSaveForm(false);
      await loadAllTemplates();
    } catch (err: any) {
      console.error("Save error:", err);
      showToast(err?.message || "Failed to save template", 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (template: SavedTemplate) => {
    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) return;

    try {
      await deleteUserTemplate(template.id);
      showToast("Template deleted from Local Storage.");
      await loadAllTemplates();
    } catch (err: any) {
      console.error("Delete error:", err);
      showToast("Failed to delete template.", 'error');
    }
  };

  const handleImportJSON = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const parsed = JSON.parse(event.target?.result as string);
        if (!parsed.template || !Array.isArray(parsed.template.elements)) {
          showToast("Invalid template JSON structure.", 'error');
          return;
        }

        const importedName = parsed.name || file.name.replace(/\.json$/i, '');
        await saveUserTemplate(
          importedName,
          parsed.template,
          parsed.layout || currentLayout,
          parsed.appUnit || currentUnit,
          parsed.description || 'Imported from JSON'
        );
        await loadAllTemplates();
        showToast(`Imported "${importedName}" successfully!`);
      } catch (err) {
        showToast("Failed to parse JSON file.", 'error');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };



  const currentList = activeTab === 'local' 
    ? localTemplates 
    : STARTER_PRESETS;

  const filteredList = currentList.filter(t => 
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <MacWindow 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Secure Local Design Template Library"
      maxWidth="max-w-4xl"
    >
      <div className="flex flex-col h-full max-h-[85vh] bg-zinc-950 text-zinc-100">
        
        {/* Toast Notification */}
        {toastMessage && (
          <div className={`px-4 py-2.5 text-xs font-semibold flex items-center justify-between shadow-lg ${
            toastMessage.type === 'error' ? 'bg-rose-600 text-white' : 'bg-emerald-600 text-white'
          }`}>
            <span className="flex items-center gap-2">
              {toastMessage.type === 'error' ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
              {toastMessage.text}
            </span>
            <button onClick={() => setToastMessage(null)} className="text-white hover:opacity-80">✕</button>
          </div>
        )}

        {/* Modal Header Bar with Secure Offline Mode Banner */}
        <div className="p-4 border-b border-zinc-800 bg-zinc-900/80 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
              <FolderOpen className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-zinc-100">Sticker Designs & Templates</h2>
                <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  <HardDrive className="w-3 h-3" /> 100% Private Offline Mode
                </span>
              </div>
              <p className="text-xs text-zinc-400">
                Your designs are saved completely locally in your browser. No data ever leaves your computer.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSaveForm(prev => !prev)}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm"
            >
              <Save className="w-3.5 h-3.5" /> Save Canvas
            </button>
            <label className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm">
              <Upload className="w-3.5 h-3.5 text-indigo-400" /> Import JSON
              <input type="file" accept=".json" onChange={handleImportJSON} className="hidden" />
            </label>
          </div>
        </div>

        {/* Save Form Flyout */}
        {showSaveForm && (
          <form onSubmit={handleSaveCurrent} className="p-4 bg-zinc-900 border-b border-zinc-800 flex flex-col gap-3 animate-in fade-in slide-in-from-top duration-200">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-zinc-200">Save Current Canvas Design</span>
              <button type="button" onClick={() => setShowSaveForm(false)} className="text-zinc-500 hover:text-zinc-300 text-xs">Cancel</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="text"
                required
                placeholder="Template Name (e.g. Luxury Wine Label 2026)"
                value={newTemplateName}
                onChange={e => setNewTemplateName(e.target.value)}
                className="px-3 py-2 bg-zinc-950 border border-zinc-700 rounded-lg text-xs text-zinc-100 focus:outline-none focus:border-blue-500"
              />
              <input
                type="text"
                placeholder="Optional description (e.g. Gold foil, 80x50mm)"
                value={newTemplateDesc}
                onChange={e => setNewTemplateDesc(e.target.value)}
                className="px-3 py-2 bg-zinc-950 border border-zinc-700 rounded-lg text-xs text-zinc-100 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="submit"
                disabled={isSaving}
                className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer shadow-sm flex items-center gap-1.5"
              >
                {isSaving ? 'Saving...' : 'Save Locally'}
              </button>
            </div>
          </form>
        )}

        {/* Navigation Tabs */}
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 bg-zinc-900/40">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('local')}
              className={`py-3 text-xs font-bold border-b-2 transition-colors cursor-pointer flex items-center gap-1.5 ${
                activeTab === 'local'
                  ? 'border-emerald-500 text-emerald-400'
                  : 'border-transparent text-zinc-400 hover:text-zinc-200'
              }`}
            >
              <HardDrive className="w-4 h-4" />
              <span>Local Storage ({localTemplates.length})</span>
            </button>
            <button
              onClick={() => setActiveTab('presets')}
              className={`py-3 text-xs font-bold border-b-2 transition-colors cursor-pointer flex items-center gap-1.5 ${
                activeTab === 'presets'
                  ? 'border-emerald-500 text-emerald-400'
                  : 'border-transparent text-zinc-400 hover:text-zinc-200'
              }`}
            >
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span>Starter Presets ({STARTER_PRESETS.length})</span>
            </button>
          </div>

          {/* Search Box */}
          <div className="relative w-48 sm:w-64 my-1.5">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1 bg-zinc-900 border border-zinc-700/80 rounded-lg text-xs text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-emerald-500"
            />
          </div>
        </div>

        {/* Templates Grid Content */}
        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
          {filteredList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-12 h-12 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-500 mb-3">
                <Layout className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-bold text-zinc-300">No templates found</h3>
              <p className="text-xs text-zinc-500 max-w-sm mt-1">
                Save your current label canvas using the "Save Canvas" button or choose one of the built-in Starter Presets!
              </p>
              <button
                onClick={() => setShowSaveForm(true)}
                className="mt-4 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-semibold cursor-pointer shadow-sm"
              >
                Save Current Design
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {filteredList.map(tpl => (
                <div
                  key={tpl.id}
                  className="p-3.5 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition-all flex flex-col justify-between group shadow-sm"
                >
                  <div>
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <h4 className="text-xs font-bold text-zinc-100 truncate flex-1" title={tpl.name}>
                        {tpl.name}
                      </h4>
                      <div className="flex items-center gap-1">
                        {tpl.isPreset && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                            Preset
                          </span>
                        )}
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 shrink-0">
                          {tpl.template.width}×{tpl.template.height}
                        </span>
                      </div>
                    </div>
                    <p className="text-[11px] text-zinc-400 line-clamp-2 mb-3 leading-relaxed">
                      {tpl.description || 'Custom VDP label template'}
                    </p>
                  </div>

                  <div className="pt-2 border-t border-zinc-800/80 flex items-center justify-between gap-2">
                    <button
                      onClick={() => {
                        onLoadTemplate(tpl.template, tpl.layout, tpl.appUnit);
                        onClose();
                      }}
                      className="px-3 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-xs font-semibold transition-colors cursor-pointer"
                    >
                      Load Template
                    </button>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => exportTemplateAsJSON(tpl)}
                        className="p-1.5 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 rounded transition-colors cursor-pointer"
                        title="Export as JSON file"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      {!tpl.isPreset && (
                        <button
                          onClick={() => handleDelete(tpl)}
                          className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors cursor-pointer"
                          title="Delete template"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-3 border-t border-zinc-800 bg-zinc-900/80 flex items-center justify-between text-xs text-zinc-500">
          <div className="flex items-center gap-2">
            <RotateCcw className="w-3.5 h-3.5 text-orange-400 cursor-pointer" onClick={() => startFreshProject()} title="Reset canvas to blank" />
            <button onClick={() => startFreshProject()} className="hover:text-zinc-300 transition-colors">Start Fresh Blank Canvas</button>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-lg text-xs font-semibold cursor-pointer"
          >
            Close
          </button>
        </div>

      </div>
    </MacWindow>
  );
};
